// RCMCRegistration.jsx
import React from "react";

export default function RCMCRegistration() {
  return (
    <div className="min-h-screen bg-white">
      {/* HERO SECTION */}
      <section className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10 items-center">
        {/* Left: Text */}
        <div>
          <p className="text-sm tracking-[0.25em] text-gray-500 uppercase mb-3">
            RCMC Registration
          </p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            RCMC Registration Support for Exporters
          </h1>
          <div className="w-20 h-1 bg-yellow-500 mb-6" />
          <p className="text-gray-700 mb-4">
            Hawkins &amp; Brooks assists exporters in obtaining RCMC
            registration from relevant Export Promotion Councils and commodity
            boards, helping them align with Foreign Trade Policy requirements
            and access export incentives.
          </p>
          <p className="text-gray-700">
            The support typically covers selecting the right council, preparing
            application details, and coordinating with council or DGFT systems
            so that RCMC is issued smoothly and recognized in trade procedures.
          </p>
        </div>

        {/* Right: Image (square) */}
        <div className="relative">
          <div className="absolute -inset-4 border border-yellow-400 rounded-3xl opacity-40 hidden md:block" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl border border-gray-100">
            <img
              src="https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Cargo ship and containers symbolising export registration"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* DOCUMENTS & WHY CHOOSE US SECTION */}
      <section className="border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-2 gap-12">
          {/* Documents required */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Documents Required for RCMC Registration
            </h2>
            <p className="text-gray-700 mb-4">
              Hawkins &amp; Brooks helps exporters compile and organise key
              details that are generally requested by export promotion councils
              for issuing RCMC.
            </p>
            <div className="bg-gray-50 border border-yellow-100 rounded-xl p-5">
              <ul className="space-y-2 text-gray-800 list-disc list-inside">
                <li>
                  IEC certificate of the exporter, with details matching
                  current trade and DGFT records.
                </li>
                <li>
                  PAN card copy of the exporting entity (company, LLP, firm, or
                  proprietor), along with basic tax details where required.
                </li>
                <li>
                  Business incorporation documents such as Certificate of
                  Incorporation, partnership deed, or registration document
                  establishing the legal status of the exporter.
                </li>
                <li>
                  Brief product or service description and primary export
                  category to map the application to the correct council or
                  board.
                </li>
                <li>
                  Authorized signatory details and basic contact information to
                  appear on the membership records and certificate.
                </li>
              </ul>
            </div>
          </div>

          {/* Why choose us */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Why Choose Hawkins &amp; Brooks?
            </h2>
            <p className="text-gray-700 mb-4">
              The firm focuses on helping exporters integrate RCMC into their
              broader export compliance and benefit-claiming framework.
            </p>
            <div className="space-y-3">
              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Export Compliance Expertise
                </h3>
                <p className="text-gray-700 text-sm">
                  Guidance on selecting the appropriate council, understanding
                  eligibility, and aligning RCMC with IEC and Foreign Trade
                  Policy requirements.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Accurate Documentation Handling
                </h3>
                <p className="text-gray-700 text-sm">
                  Assistance in preparing applications and attachments to keep
                  details consistent across IEC, tax registrations, and council
                  records.
                </p>
              </div>

              <div className="border-l-4 border-yellow-500 bg-white shadow-sm rounded-r-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Professional Advisory Support
                </h3>
                <p className="text-gray-700 text-sm">
                  Ongoing support from initial RCMC application through updates
                  and renewals, providing exporters with a steady compliance
                  partner.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-full bg-green-500 text-black font-semibold shadow hover:bg-green-400 transition-colors text-sm"
              >
                Get Consultation
              </a>
              <a
                href="tel:+919217468906"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full border border-yellow-500 text-yellow-600 font-medium hover:bg-yellow-50 transition-colors text-sm"
              >
                Call Hawkins &amp; Brooks
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
